import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { getAllPatientsActivities } from '../Services/patientActivitiesService';
import { BillingUtils } from '../Services/billingService';
import BillingKPIGeneral from '../Header/BillingKPIGeneral'; 
import BillingFiltersHeaderGeneral from '../Header/BillingFiltersHeaderGeneral';
import { 
  IconButton, 
  Box,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import { Link } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import CreateIcon from '@mui/icons-material/Create';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';

const BillingTableGeneral = ({ 
  currentUserRoles = [],
  onCreateBillableActivities,
  onActivityUpdate
}) => {
  
  // Estados principales - SEPARADOS PARA DATOS Y FILTROS
  const [allRawData, setAllRawData] = useState([]); // SOLO ACTIVIDADES DISPONIBLES PARA FACTURAR
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados de selección
  const [rowSelection, setRowSelection] = useState({});
  const [selectedActivities, setSelectedActivities] = useState([]);
  
  // Estados de filtros LOCALES - NO DISPARAN PETICIONES AL SERVIDOR
  const [localFilters, setLocalFilters] = useState({
    search: '',
    include_types: ['appointments', 'prescriptions', 'electronic_prescriptions', 'treatments', 'laboratory', 'imaging'],
    date_from: '',
    date_to: '',
    doctor_id: '',
    center_id: '',
    patient_search: '',
    min_amount: '',
    max_amount: ''
  });
  
  // Estados para carga inicial (estos SÍ pueden disparar peticiones)
  const [serverFilters, setServerFilters] = useState({
    limit: 1000, // Cargar más datos inicialmente
    offset: 0
  });
  
  // Estados de paginación FRONTEND
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25
  });
  
  // Estados de diálogos
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedActivityForDetail, setSelectedActivityForDetail] = useState(null);
  const [showCreateBillableDialog, setShowCreateBillableDialog] = useState(false);
  
  // Estado para acciones masivas
  const [isProcessingBulkAction, setIsProcessingBulkAction] = useState(false);

  // Función para obtener valor seguro
  const getSafeValue = (value, fallback = 'No especificado') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return value;
  };

  // Función para obtener nombre completo
  const getFullName = (person, fallback = 'No identificado') => {
    if (!person) return fallback;
    
    if (typeof person === 'string') {
      return person === 'unknown' ? fallback : person;
    }
    
    if (typeof person === 'object') {
      if (person.full_name) return person.full_name;
      if (person.name && person.surname) {
        return `${person.name} ${person.surname}`.trim();
      }
      if (person.name) return person.name;
      if (person._id) return `ID: ${person._id}`;
    }
    
    return fallback;
  };

  // Función para formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No especificada';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'Fecha inválida';
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  // Función para obtener tipo de actividad en español
  const getActivityTypeLabel = (type, sourceType) => {
    const types = {
      'appointments': 'Cita/Consulta',
      'prescriptions': 'Receta Tradicional', 
      'electronic_prescriptions': 'Receta Electrónica',
      'treatments': 'Tratamiento',
      'laboratory': 'Análisis de Laboratorio',
      'imaging': 'Diagnóstico por Imagen',
      'molecular': 'Medicina Molecular',
      'vaccinations': 'Vacunación',
      'medical_records': 'Registro Médico'
    };
    return types[sourceType] || types[type] || 'Otro';
  };

  // Función para transformar datos de la API - SOLO ACTIVIDADES DISPONIBLES PARA FACTURAR
const transformAllPatientsActivities = (apiResponse) => {
  console.log('🔍 [transformAllPatientsActivities] Entrada completa:', apiResponse);
  
  if (!apiResponse || !apiResponse.data || !apiResponse.data.patients || !Array.isArray(apiResponse.data.patients)) {
    console.log('❌ [transformAllPatientsActivities] Estructura de datos inválida');
    return [];
  }
  
  const allActivities = [];
  let totalProcessed = 0;
  let totalFiltered = 0;
  
  apiResponse.data.patients.forEach((patientData, patientIndex) => {
    console.log(`🔍 [transformAllPatientsActivities] Procesando paciente ${patientIndex + 1}:`, patientData.patient_info?.full_name);
    
    const patientInfo = {
      id: patientData.patient_info?._id || 'unknown',
      name: getFullName(patientData.patient_info, 'Paciente no identificado'),
      full_patient_data: patientData.patient_info
    };
    
    if (patientData.available_activities && typeof patientData.available_activities === 'object') {
      Object.entries(patientData.available_activities).forEach(([activityType, activities]) => {
        if (Array.isArray(activities) && activities.length > 0) {
          console.log(`  📋 Procesando ${activities.length} actividades de tipo ${activityType}`);
          
          activities.forEach((activity, activityIndex) => {
            totalProcessed++;
            
            // 🔒 VALIDACIÓN ESTRICTA: 5 FILTROS
            if (activity.has_billable_activity === true) {
              totalFiltered++;
              console.log(`⏭️ [FILTRO 1] Saltando actividad ${activity.element_id} - Ya tiene AF creada`);
              return;
            }

            if (activity.is_billable === false) {
              totalFiltered++;
              console.log(`⏭️ [FILTRO 2] Saltando actividad ${activity.element_id} - No es facturable`);
              return;
            }

            if (activity.existing_af_id || activity.af_id || activity.af_status) {
              totalFiltered++;
              console.log(`⏭️ [FILTRO 3] Saltando actividad ${activity.element_id} - Tiene AF existente`);
              return;
            }

            const billingStatus = activity.billing_status || activity.status;
            if (billingStatus && 
                billingStatus !== 'not_billed' && 
                billingStatus !== 'no_facturada' && 
                billingStatus !== 'disponible_para_facturar') {
              totalFiltered++;
              console.log(`⏭️ [FILTRO 4] Saltando actividad ${activity.element_id} - Estado: ${billingStatus}`);
              return;
            }

            if (activity.af_created_at || activity.billable_activity_created_at) {
              totalFiltered++;
              console.log(`⏭️ [FILTRO 5] Saltando actividad ${activity.element_id} - Tiene fecha de creación de AF`);
              return;
            }

            // ✅ EXTRAER INFORMACIÓN DE PRODUCTOS
            const productInfo = extractProductInfo(activity);
            
            console.log(`✅ [FILTROS PASADOS] Procesando actividad ${activity.element_id}`, {
              producto_cita: productInfo.producto_cita_name,
              despacho: productInfo.despacho_medico_name,
              precio_base: productInfo.producto_cita_price
            });

            // Calcular importes
            let netAmount = 0;
            let grossAmount = 0;
            let taxAmount = 0;
            
            if (activity.pricing) {
              const unitPrice = activity.pricing.unit_price || productInfo.producto_cita_price || 0;
              const qty = activity.pricing.qty || 1;
              const discount = activity.pricing.discount || 0;
              const taxRate = activity.pricing.tax_rate || 0;
              
              netAmount = (unitPrice * qty) - discount;
              taxAmount = netAmount * (taxRate / 100);
              grossAmount = netAmount + taxAmount;
            }
            
            // Información del médico
            const doctorInfo = (() => {
              const doctorData = activity.doctor_info || activity.doctor_id;
              
              if (!doctorData) {
                return { name: 'Sin médico asignado', id: 'unknown' };
              }
              
              if (typeof doctorData === 'object' && doctorData !== null) {
                return {
                  name: getFullName(doctorData, 'Médico no identificado'),
                  id: doctorData._id || 'unknown',
                  specialty: doctorData.specialty || '',
                  collegiate_number: doctorData.collegiate_number || ''
                };
              }
              
              return {
                name: `Médico ID: ${doctorData}`,
                id: doctorData
              };
            })();

            // Información del centro
            const centerInfo = (() => {
              const centerData = activity.center_info || activity.center_id || activity.medical_center;
              
              if (!centerData) {
                return { name: 'Sin centro asignado', id: 'unknown' };
              }
              
              if (typeof centerData === 'object' && centerData !== null) {
                return {
                  name: getSafeValue(centerData.name, 'Centro no identificado'),
                  id: centerData._id || 'unknown'
                };
              }
              
              return {
                name: `Centro ID: ${centerData}`,
                id: centerData
              };
            })();

            const transformed = {
              id: activity.element_id || activity._id || `${activityType}-${patientInfo.id}-${activityIndex}`,
              element_id: activity.element_id || activity._id, 
              patient_name: patientInfo.name,
              patient_id: patientInfo.id,
              
              doctor_name: doctorInfo.name,
              doctor_id: doctorInfo.id,
              doctor_specialty: doctorInfo.specialty,
              doctor_collegiate: doctorInfo.collegiate_number, 
              
              center_name: centerInfo.name,
              center_id: centerInfo.id,
              
              performed_at: formatDate(
                activity.performed_at || 
                activity.date || 
                activity.created_at ||
                activity.appointment_date ||
                activity.prescription_date
              ),
              performed_at_raw: activity.performed_at || activity.date || activity.created_at,
              
              activity_type: activityType,
              source_type: activityType,
              
              // ✅ INFORMACIÓN DE PRODUCTOS
              producto_cita_id: productInfo.producto_cita_id,
              producto_cita_name: productInfo.producto_cita_name,
              producto_cita_code: productInfo.producto_cita_code,
              producto_cita_price: productInfo.producto_cita_price,
              
              despacho_medico_id: productInfo.despacho_medico_id,
              despacho_medico_name: productInfo.despacho_medico_name,
              despacho_medico_code: productInfo.despacho_medico_code,
              
              has_product_info: productInfo.has_product_info,
              
              description: getSafeValue(
                activity.description || 
                activity.reason || 
                activity.notes ||
                activity.treatment_type ||
                activity.medication_name ||
                productInfo.producto_cita_name ||
                `${getActivityTypeLabel(null, activityType)}`,
                'Sin descripción'
              ),
              
              code: getSafeValue(
                activity.code || 
                activity.appointment_code ||
                activity.prescription_code ||
                productInfo.producto_cita_code ||
                activity.element_id,
                'Sin código'
              ),
              
              status: 'disponible_para_facturar',
              is_billable: true,
              has_billable_activity: false,
              existing_af_id: null,
              af_status: null,
              af_created_at: null,
              
              gross_amount: grossAmount,
              tax_amount: taxAmount,
              net_amount: netAmount,
              billed_net: 0,
              remaining_net: netAmount,
              
              has_authorization: !!(activity.auth_code || activity.authorization_code),
              authorization_code: getSafeValue(activity.auth_code || activity.authorization_code, ''),
              
              primary_payer_name: 'Sin asignar',
              primary_payer_percentage: 0,
              primary_payer_amount: 0,
              primary_payer_type: 'unknown',
              
              split_count: 0,
              so_links_count: 0,
              
              created_at: formatDate(activity.created_at),
              updated_at: formatDate(activity.updated_at),
              
              currency: 'EUR',
              unit_price: activity.pricing?.unit_price || productInfo.producto_cita_price || 0,
              quantity: activity.pricing?.qty || 1,
              
              _original: activity,
              _patient: patientInfo.full_patient_data,
              _activity_type_source: activityType
            };
            
            allActivities.push(transformed);
          });
        }
      });
    }
  });
  
  console.log(`📊 [transformAllPatientsActivities] Resumen de procesamiento:`);
  console.log(`   - Total procesadas: ${totalProcessed}`);
  console.log(`   - Total filtradas: ${totalFiltered}`);
  console.log(`   - Total disponibles: ${allActivities.length}`);
  
  return allActivities;
};

// ✅ NUEVA FUNCIÓN AUXILIAR: Extraer información de productos
const extractProductInfo = (activity) => {
  const productInfo = activity.product_info || {};
  
  return {
    // Producto de la cita
    producto_cita_id: productInfo.producto_cita?._id || 
                      productInfo.producto_cita_id || 
                      null,
    producto_cita_name: productInfo.producto_cita?.name || 
                        productInfo.producto_cita?.productName ||
                        productInfo.producto_cita_name || 
                        '',
    producto_cita_code: productInfo.producto_cita?.code || 
                        productInfo.producto_cita_code || 
                        '',
    producto_cita_price: parseFloat(
      productInfo.producto_cita?.price?.$numberDecimal ||
      productInfo.producto_cita?.price ||
      productInfo.producto_cita?.priceExcludingVAT?.$numberDecimal ||
      productInfo.producto_cita?.priceExcludingVAT ||
      productInfo.producto_cita_price ||
      0
    ),
    
    // Despacho médico
    despacho_medico_id: productInfo.despacho_medico?._id || 
                        productInfo.despacho_medico_id || 
                        null,
    despacho_medico_name: productInfo.despacho_medico?.name || 
                          productInfo.despacho_medico?.productName ||
                          productInfo.despacho_medico_name || 
                          '',
    despacho_medico_code: productInfo.despacho_medico?.code || 
                          productInfo.despacho_medico_code || 
                          '',
    
    // Flag
    has_product_info: !!(productInfo.producto_cita_id || productInfo.despacho_medico_id)
  };
};

  // FUNCIÓN PARA CARGA INICIAL - SOLO ACTIVIDADES DISPONIBLES PARA FACTURAR
  const loadInitialData = useCallback(async () => {
    console.log('🚀 [loadInitialData] Iniciando carga inicial...');
    setIsInitialLoading(true);
    setError(null);
    
    try {
      const queryFilters = {
        limit: serverFilters.limit,
        offset: serverFilters.offset,
        include_types: ['appointments', 'prescriptions', 'electronic_prescriptions', 'treatments', 'laboratory', 'imaging', 'molecular', 'vaccinations', 'medical_records'],
        only_unbilled: 'true', // IMPORTANTE: Como string para query params
        include_existing_af: 'false' // IMPORTANTE: Como string para query params
      };
      
      console.log('🔍 [loadInitialData] Filtros de servidor:', queryFilters);
      console.log('🌐 [loadInitialData] URL que se enviará:', `getAllPatientsActivities?${new URLSearchParams(queryFilters).toString()}`);
      
      const response = await getAllPatientsActivities(queryFilters);
      console.log('📡 [loadInitialData] Respuesta de API:', response);
      
      if (response.error) {
        throw new Error(response.error.message || 'Error al cargar actividades');
      }
      
      if (response && response.data) {
        // DEBUG: Verificar estructura de datos recibidos
        console.log('🔍 [loadInitialData] DEBUG - Estructura de datos recibidos:');
        console.log('Total patients:', response.data.patients?.length || 0);
        
        // Verificar primera actividad de cada paciente para debugging
        if (response.data.patients && response.data.patients.length > 0) {
          response.data.patients.slice(0, 2).forEach((patient, index) => {
            console.log(`🔍 [DEBUG] Paciente ${index + 1}: ${patient.patient_info?.full_name}`);
            Object.entries(patient.available_activities || {}).forEach(([type, activities]) => {
              if (Array.isArray(activities) && activities.length > 0) {
                const firstActivity = activities[0];
                console.log(`  📋 ${type} (${activities.length}): Primera actividad:`, {
                  element_id: firstActivity.element_id,
                  is_billable: firstActivity.is_billable,
                  has_billable_activity: firstActivity.has_billable_activity,
                  existing_af_id: firstActivity.existing_af_id,
                  billing_status: firstActivity.billing_status,
                  status: firstActivity.status
                });
              }
            });
          });
        }
        
        const transformedActivities = transformAllPatientsActivities(response);
        console.log('✅ [loadInitialData] Datos transformados:', transformedActivities.length);
        
        setAllRawData(transformedActivities); // GUARDAR SOLO ACTIVIDADES DISPONIBLES
        
      } else {
        console.warn('⚠️ [loadInitialData] Respuesta sin datos esperados');
        setAllRawData([]);
      }
      
    } catch (error) {
      console.error('❌ [loadInitialData] Error:', error);
      setError(error.message);
      setAllRawData([]);
    } finally {
      setIsInitialLoading(false);
    }
  }, [serverFilters]);

  // EFECTO PARA CARGA INICIAL
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // FILTRADO LOCAL
  const filteredActivities = useMemo(() => {
    console.log('🔍 [filteredActivities] Aplicando filtros locales...');
    console.log('📊 Datos originales:', allRawData.length);
    console.log('🔧 Filtros locales:', localFilters);
    
    if (!allRawData || allRawData.length === 0) {
      return [];
    }

    let filtered = [...allRawData];

    // Filtro por búsqueda general
    if (localFilters.search) {
      const searchTerm = localFilters.search.toLowerCase();
      filtered = filtered.filter(activity => {
        return (
          activity.patient_name?.toLowerCase().includes(searchTerm) ||
          activity.doctor_name?.toLowerCase().includes(searchTerm) ||
          activity.center_name?.toLowerCase().includes(searchTerm) ||
          activity.description?.toLowerCase().includes(searchTerm) ||
          activity.code?.toLowerCase().includes(searchTerm) ||
          activity.authorization_code?.toLowerCase().includes(searchTerm)
        );
      });
    }

    // Filtro por búsqueda de paciente
    if (localFilters.patient_search) {
      const patientTerm = localFilters.patient_search.toLowerCase();
      filtered = filtered.filter(activity => {
        return (
          activity.patient_name?.toLowerCase().includes(patientTerm) ||
          activity.patient_id?.toLowerCase().includes(patientTerm)
        );
      });
    }

    // Filtro por tipos de actividad
    if (localFilters.include_types && localFilters.include_types.length > 0) {
      filtered = filtered.filter(activity => 
        localFilters.include_types.includes(activity.activity_type)
      );
    }

    // Filtro por fechas
    if (localFilters.date_from) {
      const fromDate = new Date(localFilters.date_from);
      filtered = filtered.filter(activity => {
        const activityDate = new Date(activity.performed_at_raw);
        return activityDate >= fromDate;
      });
    }

    if (localFilters.date_to) {
      const toDate = new Date(localFilters.date_to);
      toDate.setHours(23, 59, 59, 999); // Final del día
      filtered = filtered.filter(activity => {
        const activityDate = new Date(activity.performed_at_raw);
        return activityDate <= toDate;
      });
    }

    // Filtro por médico
    if (localFilters.doctor_id) {
      filtered = filtered.filter(activity => 
        activity.doctor_id?.toString().includes(localFilters.doctor_id)
      );
    }

    // Filtro por centro
    if (localFilters.center_id) {
      filtered = filtered.filter(activity => 
        activity.center_id?.toString().includes(localFilters.center_id)
      );
    }

    // Filtro por importes
    if (localFilters.min_amount) {
      const minAmount = parseFloat(localFilters.min_amount);
      filtered = filtered.filter(activity => 
        activity.net_amount >= minAmount
      );
    }

    if (localFilters.max_amount) {
      const maxAmount = parseFloat(localFilters.max_amount);
      filtered = filtered.filter(activity => 
        activity.net_amount <= maxAmount
      );
    }

    console.log('✅ [filteredActivities] Resultado:', filtered.length);
    return filtered;
  }, [allRawData, localFilters]);

  // PAGINACIÓN LOCAL
  const paginatedActivities = useMemo(() => {
    const startIndex = pagination.pageIndex * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredActivities.slice(startIndex, endIndex);
  }, [filteredActivities, pagination]);

  // Calcular si hay filtros aplicados
  const isFiltered = useMemo(() => {
    return !!(
      localFilters.search ||
      localFilters.date_from ||
      localFilters.date_to ||
      localFilters.doctor_id ||
      localFilters.center_id ||
      localFilters.patient_search ||
      localFilters.min_amount ||
      localFilters.max_amount ||
      localFilters.include_types?.length !== 6 // Asumiendo que hay 6 tipos por defecto
    );
  }, [localFilters]);

  // Handlers para las acciones
  const handleCreateSingleBillable = async (activity) => {
  try {
    console.log('💼 [handleCreateSingleBillable] Creando AF individual para:', activity.id);
    await onCreateBillableActivities([activity]);
    
    // ✅ Ya está correcto
    console.log('🔄 [handleCreateSingleBillable] Recargando datos...');
    await handleReloadData();
    
    console.log('✅ [handleCreateSingleBillable] AF creada y datos recargados');
  } catch (error) {
    console.error('❌ [handleCreateSingleBillable] Error:', error);
  }
};

const handleReloadData = async () => {
  console.log('🔄 [handleReloadData] Iniciando recarga completa...');
  handleClearSelection();
  await loadInitialData();
  console.log('✅ [handleReloadData] Recarga completada');
};

  const handleFilterChange = (newFilters) => {
    console.log('🔍 [handleFilterChange] Nuevos filtros locales:', newFilters);
    setLocalFilters(newFilters);
    // Reset pagination when filters change
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleSearchChange = (searchTerm) => {
    console.log('🔍 [handleSearchChange] Nueva búsqueda:', searchTerm);
    setLocalFilters(prev => ({ ...prev, search: searchTerm }));
    // Reset pagination when search changes
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleRowSelectionChange = (updaterOrValue) => {
    console.log('🎯 [handleRowSelectionChange] Entrada recibida:', typeof updaterOrValue);
    
    // MRT puede pasar un updater function o un objeto directamente
    setRowSelection(prevSelection => {
      // Si es función, llamarla con el estado anterior
      const newSelection = typeof updaterOrValue === 'function' 
        ? updaterOrValue(prevSelection)
        : updaterOrValue;
      
      console.log('📊 [handleRowSelectionChange] Selección anterior:', prevSelection);
      console.log('📊 [handleRowSelectionChange] Nueva selección:', newSelection);
      console.log('📦 [handleRowSelectionChange] Datos paginados:', paginatedActivities.length);
      
      // Convertir índices a actividades reales
      const selectedIds = Object.keys(newSelection)
        .filter(key => newSelection[key] === true)
        .map(index => {
          const activity = paginatedActivities[parseInt(index)];
          console.log(`  ✅ Índice ${index} -> Actividad:`, activity?.id);
          return activity;
        })
        .filter(activity => activity !== undefined);
      
      console.log('✅ [handleRowSelectionChange] Total seleccionadas:', selectedIds.length);
      console.log('📋 [handleRowSelectionChange] IDs:', selectedIds.map(a => a.id));
      
      // Actualizar el estado de actividades seleccionadas
      setSelectedActivities(selectedIds);
      
      return newSelection;
    });
  };

  const handleClearSelection = () => {
    setRowSelection({});
    setSelectedActivities([]);
  };

  // const handleReloadData = () => {
  //   loadInitialData();
  //   handleClearSelection();
  // };

  const handleCreateBillableActivitiesFromHeader = async (activitiesData) => {
    setIsProcessingBulkAction(true);
    try {
      if (onCreateBillableActivities) {
        await onCreateBillableActivities(activitiesData);
      }
      
      // Clear selection and reload data
      handleClearSelection();
      await loadInitialData();
      
    } catch (error) {
      console.error('Error creating billable activities from header:', error);
      throw error; // Re-throw to let header handle the error display
    } finally {
      setIsProcessingBulkAction(false);
    }
  };

  const handleRefreshAfterInvoiceCreation = async () => {
  console.log('🔄 [BillingTableGeneral] Refrescando datos después de crear facturas');
  handleClearSelection();
  await loadInitialData();
};

  // REEMPLAZAR la función renderAmount (línea ~395) con:
const renderAmount = (amount) => {
  const value = Number(amount) || 0;
  return (
    <Box sx={{ 
      fontFamily: 'monospace', 
      textAlign: 'right',
      color: value === 0 ? '#666' : 'inherit',
      fontStyle: value === 0 ? 'italic' : 'normal',
      fontWeight: value > 0 ? 500 : 'normal'
    }}>
      {new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)}
    </Box>
  );
};

  const handleViewDetail = (activity) => {
    setSelectedActivityForDetail(activity);
    setShowDetailDialog(true);
  };

  // NUEVO: Handler para crear facturas directamente (sin crear AF primero)
const handleCreateInvoicesDirectly = async (invoicePayload) => {
  setIsProcessingBulkAction(true);
  try {
    console.log('💰 [handleCreateInvoicesDirectly] Iniciando creación de facturas');
    console.log('📦 Payload:', invoicePayload);
    
    // Este es el método que maneja el botón en BillingActionButtonGeneral
    // La lógica está en el botón, aquí solo necesitamos actualizar después
    
    // Clear selection and reload data
    handleClearSelection();
    await loadInitialData();
    
    return { success: true };
    
  } catch (error) {
    console.error('❌ Error creating invoices:', error);
    throw error;
  } finally {
    setIsProcessingBulkAction(false);
  }
};

  // COLUMNAS SIMPLIFICADAS - Solo para actividades disponibles para facturar
  // En BillingTableGeneral.jsx - Dentro del useMemo de columns (línea ~640 aprox)
const columns = useMemo(
  () => [
    {
      id: 'actions',
      header: 'Acciones',
      size: 120,
      enableColumnOrdering: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ver detalles completos">
            <IconButton 
              size="small" 
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetail(row.original);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      accessorKey: 'patient_name',
      header: 'Paciente',
      size: 200,
      Cell: ({ cell, row }) => (
        <Tooltip title={`ID Paciente: ${row.original.patient_id}`}>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {cell.getValue()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              ID: {row.original.patient_id}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'performed_at',
      header: 'Fecha',
      size: 160,
      sortingFn: (rowA, rowB) => {
        const dateA = new Date(rowA.original.performed_at_raw || 0);
        const dateB = new Date(rowB.original.performed_at_raw || 0);
        return dateA.getTime() - dateB.getTime();
      },
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: 'activity_type',
      header: 'Tipo',
      size: 180,
      Cell: ({ cell, row }) => (
        <Chip 
          size="small"
          label={getActivityTypeLabel(cell.getValue(), row.original.source_type)}
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      accessorKey: 'description',
      header: 'Descripción',
      size: 200,
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue()} placement="top">
          <Box sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            maxWidth: '190px'
          }}>
            {cell.getValue()}
          </Box>
        </Tooltip>
      ),
    },    
    {
      accessorKey: 'net_amount',
      header: 'Monto Neto',
      size: 160,
      sortingFn: 'basic',
      Cell: ({ cell }) => {
        const value = Number(cell.getValue()) || 0;
        return (
          <Box sx={{ 
            fontFamily: 'monospace', 
            textAlign: 'right',
            fontWeight: 500
          }}>
            {new Intl.NumberFormat('es-ES', {
              style: 'currency',
              currency: 'EUR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value)}
          </Box>
        );
      },
    },
    {
      accessorKey: 'doctor_name',
      header: 'Médico',
      size: 160,
      Cell: ({ cell, row }) => (
        <Tooltip title={`ID Médico: ${row.original.doctor_id}`}>
          <Box sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            maxWidth: '150px'
          }}>
            {cell.getValue()}
          </Box>
        </Tooltip>
      ),
    },
{
  accessorKey: 'despacho_medico_name',
  header: 'Despacho Médico',
  size: 180,
  Cell: ({ row }) => {
    const despachoName = row.original.despacho_medico_name;
    const despachoCode = row.original.despacho_medico_code;
    
    if (!despachoName) {
      return (
        <Typography variant="caption" color="textSecondary" fontStyle="italic">
          Sin despacho asignado
        </Typography>
      );
    }
    
    return (
      <Box>
        <Tooltip title={`Código: ${despachoCode || 'N/A'}`}>
          <Typography variant="body2" fontWeight="medium">
            {despachoName}
          </Typography>
        </Tooltip>
        {despachoCode && (
          <Typography variant="caption" color="textSecondary">
            Código: {despachoCode}
          </Typography>
        )}
      </Box>
    );
  },
},
  ],
  []
);

  // Detail Dialog Component (sin cambios)
  const renderDetailDialog = () => {
    if (!selectedActivityForDetail) return null;

    const activity = selectedActivityForDetail;

    return (
      <Dialog 
        open={showDetailDialog} 
        onClose={() => setShowDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            <Typography variant="h6">Detalles de la Actividad</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Patient Information */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Información del Paciente
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Nombre Completo</Typography>
                      <Typography variant="body1">{activity.patient_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">ID del Paciente</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {activity.patient_id}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Medical Information */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalHospitalIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Información Médica
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Médico</Typography>
                      <Typography variant="body1">{activity.doctor_name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {activity.doctor_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Centro Médico</Typography>
                      <Typography variant="body1">{activity.center_name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {activity.center_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Fecha de Realización</Typography>
                      <Typography variant="body1">{activity.performed_at}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Tipo de Actividad</Typography>
                      <Chip 
                        label={getActivityTypeLabel(activity.activity_type, activity.source_type)}
                        color="primary"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">Descripción</Typography>
                      <Typography variant="body1">{activity.description}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Código</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {activity.code}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Código de Autorización</Typography>
                      {activity.authorization_code ? (
                        <Chip 
                          label={activity.authorization_code}
                          color="success"
                          variant="outlined"
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary" fontStyle="italic">
                          Sin autorización
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* ✅ NUEVA SECCIÓN: Información de Productos/Servicios */}
{activity.has_product_info && (
  <Grid item xs={12}>
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium">
            Productos/Servicios Asociados
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Producto de la Cita */}
          {activity.producto_cita_name && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                  Producto/Servicio de la Cita
                </Typography>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Nombre</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {activity.producto_cita_name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle2" color="textSecondary">Código</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {activity.producto_cita_code || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="subtitle2" color="textSecondary">Precio Base</Typography>
                <Typography variant="body1" fontWeight="medium" color="primary">
                  {new Intl.NumberFormat('es-ES', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(activity.producto_cita_price || 0)}
                </Typography>
              </Grid>
            </>
          )}

          {/* Despacho Médico */}
          {activity.despacho_medico_name && (
            <>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                  Despacho Médico
                </Typography>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Nombre</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {activity.despacho_medico_name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Código</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {activity.despacho_medico_code || 'N/A'}
                </Typography>
              </Grid>
            </>
          )}

          {/* Si no hay productos */}
          {!activity.producto_cita_name && !activity.despacho_medico_name && (
            <Grid item xs={12}>
              <Alert severity="info">
                No hay información de productos/servicios disponible para esta actividad.
              </Alert>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  </Grid>
)}

            {/* Billing Information */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoneyIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Información de Facturación
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Estado</Typography>
                      <Chip 
                        label="Disponible para Facturar"
                        color="success"
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Pagador Principal</Typography>
                      <Typography variant="body1">{activity.primary_payer_name}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Desglose de Importes
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell><strong>Precio Unitario</strong></TableCell>
                              <TableCell align="right">{renderAmount(activity.unit_price)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Cantidad</strong></TableCell>
                              <TableCell align="right">{activity.quantity}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Monto Neto</strong></TableCell>
                              <TableCell align="right">{renderAmount(activity.net_amount)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Impuestos</strong></TableCell>
                              <TableCell align="right">{renderAmount(activity.tax_amount)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Monto Bruto</strong></TableCell>
                              <TableCell align="right">{renderAmount(activity.gross_amount)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Información Adicional
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Fecha de Creación</Typography>
                      <Typography variant="body1">{activity.created_at}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Última Actualización</Typography>
                      <Typography variant="body1">{activity.updated_at}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">Moneda</Typography>
                      <Typography variant="body1">{activity.currency}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">ID de la Actividad</Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {activity.id}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowDetailDialog(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleReloadData}>
            Reintentar
          </Button>
        }>
          Error al cargar las actividades: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <div>      
      {/* KPIs Header - Simplificado para solo actividades disponibles */}
      <BillingKPIGeneral 
        allActivities={filteredActivities}
        selectedActivities={selectedActivities}
        onSelectionClear={handleClearSelection}
        isFiltered={isFiltered}
        isLoading={isInitialLoading}
      />

      {/* Filters Header - Simplificado */}
      <BillingFiltersHeaderGeneral
        currentUserRoles={currentUserRoles}
        filters={localFilters}
        onFiltersChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        selectedActivities={selectedActivities}
        onCreateBillableActivities={handleCreateBillableActivitiesFromHeader}
        refreshData={handleRefreshAfterInvoiceCreation} 
        onReloadData={handleReloadData}
        onClearSelection={handleClearSelection}
        isLoading={isInitialLoading || isProcessingBulkAction}
      />

      {/* Main Table - Solo actividades disponibles para facturar */}
      <Box sx={{ mt: 0 }}>
        <MaterialReactTable
          columns={columns}
          data={paginatedActivities}
          enableRowSelection
          getRowId={(originalRow, index) => index.toString()}
          onRowSelectionChange={handleRowSelectionChange}
          state={{
            rowSelection,
            pagination
          }}
          onPaginationChange={setPagination}
          manualPagination
          rowCount={filteredActivities.length}
          
          // UI Configuration
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
            }
          }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 'bold',
              fontSize: '0.875rem'
            }
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => handleViewDetail(row.original),
            sx: {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }
          })}
          muiTableBodyCellProps={{
            sx: {
              fontSize: '0.875rem',
              py: 1
            }
          }}
          
          // Features Configuration
          enableColumnOrdering
          enableColumnResizing
          enableDensityToggle
          enableFullScreenToggle
          enableGlobalFilter={true}
          enableColumnFilters={true}
          enableTopToolbar={true}
          enableBottomToolbar={true}
          
          // Icons
          icons={{
            FilterListIcon,
            EditIcon: CreateIcon,
            SearchIcon,
            ViewColumnIcon,
            FullscreenIcon,
            FullscreenExitIcon,
            SyncAltIcon: UnfoldMoreIcon,
            DensityMediumIcon
          }}
          
          // Localization
          localization={{
            actions: 'Acciones',
            and: 'y',
            cancel: 'Cancelar',
            changeFilterMode: 'Cambiar modo de filtro',
            changeSearchMode: 'Cambiar modo de búsqueda',
            clearFilter: 'Limpiar filtro',
            clearSearch: 'Limpiar búsqueda',
            clearSort: 'Limpiar ordenación',
            clickToCopy: 'Clic para copiar',
            collapse: 'Colapsar',
            collapseAll: 'Colapsar todo',
            columnActions: 'Acciones de columna',
            copiedToClipboard: 'Copiado al portapapeles',
            dropToGroupBy: 'Arrastra para agrupar por {column}',
            edit: 'Editar',
            expand: 'Expandir',
            expandAll: 'Expandir todo',
            filterArrIncludes: 'Incluye',
            filterArrIncludesAll: 'Incluye todos',
            filterArrIncludesSome: 'Incluye algunos',
            filterBetween: 'Entre',
            filterBetweenInclusive: 'Entre (inclusivo)',
            filterByColumn: 'Filtrar por {column}',
            filterContains: 'Contiene',
            filterEmpty: 'Vacío',
            filterEndsWith: 'Termina con',
            filterEquals: 'Igual a',
            filterEqualsString: 'Igual a',
            filterFuzzy: 'Fuzzy',
            filterGreaterThan: 'Mayor que',
            filterGreaterThanOrEqualTo: 'Mayor o igual que',
            filterInNumberRange: 'Entre',
            filterIncludesString: 'Contiene',
            filterIncludesStringSensitive: 'Contiene',
            filterLessThan: 'Menor que',
            filterLessThanOrEqualTo: 'Menor o igual que',
            filterMode: 'Modo de filtro: {filterType}',
            filterNotEmpty: 'No vacío',
            filterNotEquals: 'No igual a',
            filterStartsWith: 'Comienza con',
            filterWeakEquals: 'Igual a',
            filteringByColumn: 'Filtrando por {column} - {filterType} - {filterValue}',
            goToFirstPage: 'Ir a la primera página',
            goToLastPage: 'Ir a la última página',
            goToNextPage: 'Ir a la página siguiente',
            goToPreviousPage: 'Ir a la página anterior',
            grab: 'Agarrar',
            groupByColumn: 'Agrupar por {column}',
            groupedBy: 'Agrupado por',
            hideAll: 'Ocultar todo',
            hideColumn: 'Ocultar columna {column}',
            max: 'Máximo',
            min: 'Mínimo',
            move: 'Mover',
            noRecordsToDisplay: 'No hay actividades disponibles para facturar',
            noResultsFound: 'No se encontraron actividades disponibles',
            of: 'de',
            or: 'o',
            pinToLeft: 'Anclar a la izquierda',
            pinToRight: 'Anclar a la derecha',
            resetColumnSize: 'Restablecer tamaño de columna',
            resetOrder: 'Restablecer orden',
            rowActions: 'Acciones de fila',
            rowNumber: '#',
            rowNumbers: 'Números de fila',
            rowsPerPage: 'Filas por página',
            save: 'Guardar',
            search: 'Buscar',
            select: 'Seleccionar',
            selectedCountOfRowCountRowsSelected: '{selectedCount} de {rowCount} actividad(es) seleccionada(s)',
            showAll: 'Mostrar todo',
            showAllColumns: 'Mostrar todas las columnas',
            showHideColumns: 'Mostrar/Ocultar columnas',
            showHideFilters: 'Mostrar/Ocultar filtros',
            showHideSearch: 'Mostrar/Ocultar búsqueda',
            sortByColumnAsc: 'Ordenar por {column} ascendente',
            sortByColumnDesc: 'Ordenar por {column} descendente',
            sortedByColumnAsc: 'Ordenado por {column} ascendente',
            sortedByColumnDesc: 'Ordenado por {column} descendente',
            thenBy: ', luego por ',
            toggleDensity: 'Alternar densidad',
            toggleFullScreen: 'Alternar pantalla completa',
            toggleSelectAll: 'Alternar seleccionar todo',
            toggleSelectRow: 'Alternar seleccionar fila',
            toggleVisibility: 'Alternar visibilidad',
            ungroupByColumn: 'Desagrupar por {column}',
            unpin: 'Desanclar',
            unpinAll: 'Desanclar todo'
          }}
          
          // Initial State
          initialState={{
            pagination: { pageIndex: 0, pageSize: 25 },
            density: 'compact',
            columnPinning: { left: ['mrt-row-select', 'actions'] }
          }}
        />
      </Box>

      {/* Detail Dialog */}
      {renderDetailDialog()}
    </div>
  );
};

export default BillingTableGeneral;